import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, Eye, Landmark, Layers, Stethoscope } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { DetailDialog, DetailRow } from "@/components/ui/detail-dialog";
import { UserName, useUser } from "@/components/common/EntityName";
import { revenueApi } from "@/lib/api";
import type { DoctorRevenue } from "@/lib/api/types";
import { fmtCurrency, fmtNumber } from "@/lib/utils";
import { usePagedQuery, PAGE_SIZE } from "@/lib/usePagedQuery";

export default function RevenuePage() {
  const [detail, setDetail] = useState<DoctorRevenue | null>(null);

  const platform = useQuery({
    queryKey: ["revenue", "platform"],
    queryFn: () => revenueApi.platform().then((r) => r.data.data),
  });
  const top = useQuery({
    queryKey: ["revenue", "top"],
    queryFn: () => revenueApi.topDoctors(5).then((r) => r.data.data ?? []),
  });

  const {
    page, setPage, items, totalItems, totalPages, isFetching,
  } = usePagedQuery<DoctorRevenue>({
    key: ["revenue", "doctors"],
    fetcher: revenueApi.doctors,
  });

  const totalDoctorEarnings = top.data?.reduce((s, d) => s + d.totalEarnings, 0) ?? 0;

  return (
    <>
      <PageHeader
        title="Revenue"
        subtitle="Platform commission plus per-doctor earnings — the money view of every beneficiary."
      />

      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Platform commission</span>
              <Landmark className="size-4 text-primary" />
            </div>
            <p className="mt-2 text-2xl font-extrabold">
              {platform.data ? fmtCurrency(platform.data.totalCommission, "TZS") : "…"}
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">Afya24's cut of every paid service</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Commission records</span>
              <Layers className="size-4 text-primary" />
            </div>
            <p className="mt-2 text-2xl font-extrabold">{platform.data ? fmtNumber(platform.data.count) : "…"}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">Paid consultations & sales recorded</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Top-5 doctor earnings</span>
              <Stethoscope className="size-4 text-primary" />
            </div>
            <p className="mt-2 text-2xl font-extrabold">{top.data ? fmtCurrency(totalDoctorEarnings, "TZS") : "…"}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">Combined take-home of the top earners</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Earnings per doctor</CardTitle>
          <CardDescription>
            Every doctor with recorded commissions. Tap a row for the gross / commission / take-home breakdown.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <EmptyState
              icon={TrendingUp}
              title={isFetching ? "Loading…" : "No revenue records yet"}
              description="Earnings appear here after the first paid consultation or product sale."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Doctor</TableHead>
                  <TableHead>Gross billed</TableHead>
                  <TableHead>Platform commission</TableHead>
                  <TableHead>Doctor take-home</TableHead>
                  <TableHead>Paid items</TableHead>
                  <TableHead className="text-right">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((d) => (
                  <TableRow key={d.doctorId} className="cursor-pointer" onClick={() => setDetail(d)}>
                    <TableCell className="text-sm font-semibold"><UserName id={d.doctorId} /></TableCell>
                    <TableCell className="text-sm">{fmtCurrency(d.totalGross, "TZS")}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{fmtCurrency(d.commissionPaid, "TZS")}</TableCell>
                    <TableCell className="text-sm font-bold">{fmtCurrency(d.totalEarnings, "TZS")}</TableCell>
                    <TableCell className="text-sm">{d.count}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="outline" onClick={() => setDetail(d)}>
                        <Eye className="size-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Pagination page={page} size={PAGE_SIZE} totalItems={totalItems} totalPages={totalPages} onChange={setPage} />
        </CardContent>
      </Card>

      <DoctorRevenueDialog rev={detail} onClose={() => setDetail(null)} />
    </>
  );
}

function DoctorRevenueDialog({ rev, onClose }: { rev: DoctorRevenue | null; onClose: () => void }) {
  const { data: user } = useUser(rev?.doctorId);
  if (!rev) return null;
  const pct = rev.totalGross > 0 ? Math.round((rev.commissionPaid / rev.totalGross) * 100) : 0;
  return (
    <DetailDialog
      open={!!rev}
      onClose={onClose}
      icon={TrendingUp}
      title={user?.name ?? "Doctor earnings"}
      subtitle={user?.specialization ?? undefined}
    >
      <div className="mb-3 rounded-xl border border-border bg-muted/40 p-4 text-center">
        <p className="text-[10px] font-bold uppercase text-muted-foreground">Take-home earnings</p>
        <p className="text-2xl font-extrabold">{fmtCurrency(rev.totalEarnings, "TZS")}</p>
      </div>
      <DetailRow label="Gross billed" value={fmtCurrency(rev.totalGross, "TZS")} />
      <DetailRow label="Platform commission" value={`${fmtCurrency(rev.commissionPaid, "TZS")} (${pct}%)`} />
      <DetailRow label="Paid consultations / sales" value={String(rev.count)} />
      <DetailRow label="Email" value={user?.email ?? "—"} />
      <DetailRow label="Phone" value={user?.phoneNumber ?? "—"} />
    </DetailDialog>
  );
}
